import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  updatePassword,
  linkWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail,
  updateEmail
} from "firebase/auth";
import { auth } from "../../firebase/firebase";
import { useEffect, useState } from "react";
import { 
  checkSellerExistsByPhone, 
  checkSellerExistsByEmail, 
  getSellerEmailByPhone, 
  checkSellerByUserId, 
  ensureUserDocument, 
  updateSeller
} from "./firestoreService";

// Helper function to get user-friendly error messages for reCAPTCHA errors
const getRecaptchaErrorMessage = (error) => {
  const errorCode = error?.code || "";
  const errorMessage = error?.message || "";

  // Check for hostname/captcha-check-failed errors
  if (
    errorCode === "auth/captcha-check-failed" ||
    errorMessage.includes("captcha-check-failed") ||
    errorMessage.includes("Hostname match not found")
  ) {
    const currentHost = typeof window !== "undefined" ? window.location.hostname : "unknown";
    return `Domain authorization error: The domain "${currentHost}" is not authorized in Firebase Console. Please add this domain to Firebase Console → Authentication → Settings → Authorized domains. For localhost, add "localhost".`;
  }

  // Check for other common reCAPTCHA errors
  if (errorCode === "auth/too-many-requests") {
    return "Too many requests. Please wait a moment and try again.";
  }

  if (errorCode === "auth/invalid-phone-number") {
    return "Invalid phone number. Please check and try again.";
  }

  if (errorCode === "auth/quota-exceeded") {
    return "SMS quota exceeded. Please try again later or contact support.";
  }

  if (errorCode === "auth/invalid-app-credential") {
    return "App verification failed. This usually happens if the domain is not whitelisted in Firebase Console. Please add 'localhost' to Authorized Domains in Firebase Console -> Authentication -> Settings.";
  }

  // Return original message if no specific match
  return errorMessage || "Failed to send OTP. Please try again.";
};

// Helper function to ensure DOM is ready and browser environment
const ensureDOMReady = () => {
  return new Promise((resolve) => {
    // Check if we're in browser environment
    if (typeof window === "undefined" || typeof document === "undefined") {
      throw new Error("reCAPTCHA requires a browser environment");
    }

    // If DOM is already ready, resolve immediately
    if (document.readyState === "complete" || document.readyState === "interactive") {
      resolve();
      return;
    }

    // Wait for DOM to be ready
    if (document.addEventListener) {
      document.addEventListener("DOMContentLoaded", () => resolve(), { once: true });
    } else {
      // Fallback for older browsers
      window.attachEvent("onload", () => resolve());
    }
  });
};

// Helper function to setup reCAPTCHA container
const setupRecaptchaContainer = () => {
  // Remove existing container to prevent "already rendered" errors
  const existingContainer = document.getElementById("recaptcha-container");
  if (existingContainer) {
    // Clear any existing reCAPTCHA widgets
    try {
      const widgets = window.grecaptcha;
      if (widgets && widgets.getResponse) {
        const widgetId = existingContainer.getAttribute("data-widget-id");
        if (widgetId) {
          widgets.reset(parseInt(widgetId));
        }
      }
    } catch {
      // Ignore errors during cleanup
    }
    existingContainer.remove();
  }

  // Create fresh container with unique ID
  const recaptchaContainer = document.createElement("div");
  recaptchaContainer.id = "recaptcha-container";
  // Do not hide the container, as it may prevent the reCAPTCHA challenge from appearing
  // The invisible reCAPTCHA badge will be positioned automatically
  document.body.appendChild(recaptchaContainer);

  return recaptchaContainer;
};

// Helper function to cleanup reCAPTCHA verifier
const cleanupRecaptchaVerifier = () => {
  const existingVerifier = window.recaptchaVerifier;
  if (existingVerifier) {
    try {
      existingVerifier.clear();
    } catch (e) {
      // Ignore cleanup errors
      console.warn("Error clearing reCAPTCHA verifier:", e);
    }
    delete window.recaptchaVerifier;
  }
  
  // Also remove the container element to prevent "already rendered" errors
  const container = document.getElementById("recaptcha-container");
  if (container) {
    try {
      // Clear any reCAPTCHA widgets in the container
      const widgets = window.grecaptcha;
      if (widgets && widgets.getResponse) {
        const widgetId = container.getAttribute("data-widget-id");
        if (widgetId) {
          widgets.reset(parseInt(widgetId));
        }
      }
    } catch {
      // Ignore errors
    }
    container.remove();
  }
};

// Send OTP to phone number
export const sendOTP = async (phoneNumber) => {
  try {
    // Ensure we're in browser environment and DOM is ready
    await ensureDOMReady();

    // Format phone number with +91 if not already formatted
    const formattedPhone = phoneNumber.startsWith("+") ? phoneNumber : `+91${phoneNumber}`;
    
    // Check if seller exists
    const sellerExists = await checkSellerExistsByPhone(formattedPhone);
    if (!sellerExists) {
      return { confirmationResult: null, error: "No seller account found with this phone number" };
    }

    // Setup reCAPTCHA container
    setupRecaptchaContainer();

    // Cleanup any existing verifier
    cleanupRecaptchaVerifier();

    // Create new verifier with error handling
    let recaptchaVerifier;
    try {
      // Ensure container exists and is clean
      setupRecaptchaContainer();
      
      recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {
          // reCAPTCHA solved
        },
        "expired-callback": () => {
          // reCAPTCHA expired - cleanup and allow retry
          cleanupRecaptchaVerifier();
        },
      });

      window.recaptchaVerifier = recaptchaVerifier;
    } catch (verifierError) {
      cleanupRecaptchaVerifier();
      // Check for "already rendered" error and provide helpful message
      if (verifierError?.message?.includes("already been rendered") || 
          verifierError?.message?.includes("already rendered")) {
        // Force cleanup and retry once
        cleanupRecaptchaVerifier();
        // Wait a bit before retrying
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
          setupRecaptchaContainer();
          recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
            size: "invisible",
            callback: () => {},
            "expired-callback": () => {
              cleanupRecaptchaVerifier();
            },
          });
          window.recaptchaVerifier = recaptchaVerifier;
        } catch (retryError) {
          const errorMsg = getRecaptchaErrorMessage(retryError);
          return { confirmationResult: null, error: errorMsg };
        }
      } else {
        const errorMsg = getRecaptchaErrorMessage(verifierError);
        return { confirmationResult: null, error: errorMsg };
      }
    }

    // Send OTP with phone number
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
      return { confirmationResult, error: null };
    } catch (otpError) {
      cleanupRecaptchaVerifier();
      const errorMsg = getRecaptchaErrorMessage(otpError);
      return { confirmationResult: null, error: errorMsg };
    }
  } catch (error) {
    // Clean up verifier on any error
    cleanupRecaptchaVerifier();
    
    // Check if it's a browser environment error
    if (error.message && error.message.includes("browser environment")) {
      return { confirmationResult: null, error: "This feature requires a browser environment." };
    }
    
    const errorMsg = getRecaptchaErrorMessage(error);
    return { confirmationResult: null, error: errorMsg };
  }
};

// Send OTP for signup (doesn't check for existing seller)
export const sendOTPForSignup = async (phoneNumber) => {
  try {
    // Ensure we're in browser environment and DOM is ready
    await ensureDOMReady();

    // Format phone number with +91 if not already formatted
    const formattedPhone = phoneNumber.startsWith("+") ? phoneNumber : `+91${phoneNumber}`;
    
    // Setup reCAPTCHA container
    setupRecaptchaContainer();

    // Cleanup any existing verifier
    cleanupRecaptchaVerifier();

    // Create new verifier with error handling
    let recaptchaVerifier;
    try {
      // Ensure container exists and is clean
      setupRecaptchaContainer();
      
      recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {
          // reCAPTCHA solved
        },
        "expired-callback": () => {
          // reCAPTCHA expired - cleanup and allow retry
          cleanupRecaptchaVerifier();
        },
      });

      window.recaptchaVerifier = recaptchaVerifier;
    } catch (verifierError) {
      cleanupRecaptchaVerifier();
      // Check for "already rendered" error and provide helpful message
      if (verifierError?.message?.includes("already been rendered") || 
          verifierError?.message?.includes("already rendered")) {
        // Force cleanup and retry once
        cleanupRecaptchaVerifier();
        // Wait a bit before retrying
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
          setupRecaptchaContainer();
          recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
            size: "invisible",
            callback: () => {},
            "expired-callback": () => {
              cleanupRecaptchaVerifier();
            },
          });
          window.recaptchaVerifier = recaptchaVerifier;
        } catch (retryError) {
          const errorMsg = getRecaptchaErrorMessage(retryError);
          return { confirmationResult: null, error: errorMsg };
        }
      } else {
        const errorMsg = getRecaptchaErrorMessage(verifierError);
        return { confirmationResult: null, error: errorMsg };
      }
    }

    // Send OTP with phone number
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
      return { confirmationResult, error: null };
    } catch (otpError) {
      cleanupRecaptchaVerifier();
      const errorMsg = getRecaptchaErrorMessage(otpError);
      return { confirmationResult: null, error: errorMsg };
    }
  } catch (error) {
    // Clean up verifier on any error
    cleanupRecaptchaVerifier();
    
    // Check if it's a browser environment error
    if (error.message && error.message.includes("browser environment")) {
      return { confirmationResult: null, error: "This feature requires a browser environment." };
    }
    
    const errorMsg = getRecaptchaErrorMessage(error);
    return { confirmationResult: null, error: errorMsg };
  }
};

// Verify OTP code (for login - checks seller exists)
export const verifyOTP = async (confirmationResult, code) => {
  try {
    const userCredential = await confirmationResult.confirm(code);
    
    // Check if seller exists in Firestore by userId
    const { seller, error } = await checkSellerByUserId(userCredential.user.uid);
    if (error || !seller) {
      // User authenticated but no seller record - sign them out
      await signOut(auth);
      return { user: null, error: "No seller account found. Please contact support." };
    }

    // Clean up recaptcha verifier
    cleanupRecaptchaVerifier();

    return { user: userCredential.user, error: null };
  } catch (error) {
    return { user: null, error: error.message || "Invalid OTP code" };
  }
};

// Verify OTP code for signup (doesn't require seller to exist)
export const verifyOTPForSignup = async (confirmationResult, code) => {
  try {
    // Ensure code is a string
    const codeStr = String(code).trim();
    
    console.log("Verifying OTP...", { codeLength: codeStr.length });
    
    if (!confirmationResult || !confirmationResult.confirm) {
      throw new Error("Invalid verification session. Please resend OTP.");
    }

    const userCredential = await confirmationResult.confirm(codeStr);

    // Clean up recaptcha verifier
    cleanupRecaptchaVerifier();

    return { user: userCredential.user, error: null };
  } catch (error) {
    console.error("OTP Verification Error:", error);
    
    // Clean up verifier on any error
    cleanupRecaptchaVerifier();
    
    let errorMessage = error.message || "Invalid OTP code";
    
    if (error.code === "auth/invalid-verification-code") {
      errorMessage = "The OTP code entered is incorrect. Please check and try again.";
    } else if (error.code === "auth/code-expired") {
      errorMessage = "The OTP code has expired. Please resend a new code.";
    } else if (error.code === "auth/missing-verification-code") {
      errorMessage = "Please enter the verification code.";
    } else if (error.message.includes("BAD_REQUEST") || error.message.includes("400")) {
      errorMessage = "Invalid request. Please ensure the code is correct or try resending.";
    }

    return { user: null, error: errorMessage };
  }
};

// Email/Password login (legacy support)
export const loginWithEmail = async (email, password) => {
  try {
    // We attempt to sign in directly without checking Firestore first.
    // This allows login with placeholder emails (phone@...) which don't exist in Firestore.
    // Validity of the account is checked by signInWithEmailAndPassword and then checkSellerByUserId.

    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Verify seller exists in Firestore by userId
    // We don't block login if seller is missing here. 
    // Instead, we let them login and handlePostAuthRouting will redirect to onboarding.
    const { seller, error: _error } = await checkSellerByUserId(userCredential.user.uid);
    
    // Self-healing: Update seller email in Firestore if missing or different from Auth email
    // This ensures that future logins with Phone+Password will work correctly
    if (seller && userCredential.user.email && (!seller.email || seller.email !== userCredential.user.email)) {
      // Don't await this - let it happen in background to not slow down login
      updateSeller(userCredential.user.uid, { email: userCredential.user.email })
        .catch(err => console.warn("Background email sync failed:", err));
    }

    return { user: userCredential.user, error: null };
  } catch (error) {
    // Provide user-friendly error messages
    if (error.code === "auth/user-not-found" || error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
      return { user: null, error: "Invalid email or password." };
    }
    return { user: null, error: error.message };
  }
};

// Login with password (phone or email)
export const loginWithPassword = async (phoneOrEmail, password) => {
  try {
    // Determine if it's phone or email
    const isEmail = phoneOrEmail.includes("@");
    
    if (isEmail) {
      return await loginWithEmail(phoneOrEmail, password);
    } else {
      // For phone number, get seller's email from Firestore and use email/password auth
      const formattedPhone = phoneOrEmail.startsWith("+") ? phoneOrEmail : `+91${phoneOrEmail}`;
      const sellerExists = await checkSellerExistsByPhone(formattedPhone);
      
      if (!sellerExists) {
        return { 
          user: null, 
          error: "No seller account found with this phone number. Please use OTP login or sign up first." 
        };
      }

      // Get seller's email from Firestore
      const sellerEmail = await getSellerEmailByPhone(formattedPhone);
      
      let emailToLogin = sellerEmail;
      
      if (!emailToLogin) {
        // If no email found in Firestore, try the placeholder email pattern
        // This handles cases where user signed up with phone only
        const cleanPhone = formattedPhone.replace(/\D/g, "");
        emailToLogin = `${cleanPhone}@no-email.reelkart.com`;
      }

      // Use email/password authentication
      const result = await loginWithEmail(emailToLogin, password);

      // Retry logic: If login failed with the stored email, try the placeholder email
      // This handles cases where the user's Auth email is different from Firestore email
      if (result.error && sellerEmail && emailToLogin !== `${formattedPhone.replace(/\D/g, "")}@no-email.reelkart.com`) {
        const cleanPhone = formattedPhone.replace(/\D/g, "");
        const placeholderEmail = `${cleanPhone}@no-email.reelkart.com`;
        
        // Only try if the failed email was different from the placeholder
        if (emailToLogin !== placeholderEmail) {
          const fallbackResult = await loginWithEmail(placeholderEmail, password);
          // If fallback worked, return it. If not, return the original error (or the new one)
          if (!fallbackResult.error) {
            return fallbackResult;
          }
        }
      }

      return result;
    }
  } catch (error) {
    // Provide more helpful error messages
    if (error.code === "auth/user-not-found") {
      return { user: null, error: "No account found with this phone number. Please check and try again." };
    } else if (error.code === "auth/wrong-password") {
      return { user: null, error: "Incorrect password. Please try again or use 'Forgot Password' to reset." };
    } else if (error.code === "auth/invalid-email") {
      return { user: null, error: "Invalid email address. Please check and try again." };
    }
    return { user: null, error: error.message || "Login failed. Please try again." };
  }
};

// Send password reset email
export const sendPasswordResetEmailToUser = async (email) => {
  try {
    // Validate email format
    if (!email || !email.includes("@")) {
      return { success: false, error: "Please enter a valid email address" };
    }

    // Check if seller exists with this email
    const sellerExists = await checkSellerExistsByEmail(email);
    if (!sellerExists) {
      // Still try to send the email (Firebase will handle if user doesn't exist)
      // This prevents email enumeration attacks
      try {
        await sendPasswordResetEmail(auth, email);
        return { success: true, error: null };
      } catch (err) {
        // If email doesn't exist in Firebase Auth, provide helpful message
        if (err.code === "auth/user-not-found") {
          return { success: false, error: "No account found with this email address." };
        }
        throw err;
      }
    }

    // Send password reset email
    await sendPasswordResetEmail(auth, email);
    return { success: true, error: null };
  } catch (error) {
    // Provide user-friendly error messages
    if (error.code === "auth/user-not-found") {
      return { success: false, error: "No account found with this email address." };
    } else if (error.code === "auth/invalid-email") {
      return { success: false, error: "Invalid email address. Please check and try again." };
    } else if (error.code === "auth/too-many-requests") {
      return { success: false, error: "Too many requests. Please try again later." };
    }
    return { success: false, error: error.message || "Failed to send password reset email. Please try again." };
  }
};

// Set password for user after OTP login
export const setPasswordForUser = async (user, newPassword) => {
  try {
    // Check if user has email
    if (!user.email) {
      return { success: false, error: "User account must have an email to set password" };
    }

    // If user doesn't have a password yet, we need to create email/password credential
    const providers = user.providerData.map(p => p.providerId);
    
    if (providers.includes("password")) {
      // User already has password, update it
      await updatePassword(user, newPassword);
      return { success: true, error: null };
    } else {
      // User only has phone auth - we can't directly add password
      // We need to create a new account with email/password and link it
      // For now, return error suggesting they need to add email first
      return { success: false, error: "Please add an email to your account first to set a password" };
    }
  } catch (error) {
    return { success: false, error: error.message || "Failed to set password" };
  }
};

// Link email/password credential to phone auth user (for signup)
export const linkEmailPasswordToPhoneUser = async (user, email, password) => {
  try {
    // Generate placeholder email if not provided
    let emailToLink = email;
    if (!emailToLink) {
      // Create a placeholder email using the user's phone number
      const phoneNumber = user.phoneNumber || user.uid; // Fallback to UID if phone missing
      const cleanPhone = phoneNumber.replace(/\D/g, "");
      emailToLink = `${cleanPhone}@no-email.reelkart.com`;
    }

    // Check if user already has email/password provider
    const providers = user.providerData.map(p => p.providerId);
    if (providers.includes("password")) {
      return { success: true, error: null }; // Already has password
    }

    // Update user's email if not set or different
    if (!user.email || user.email !== emailToLink) {
      try {
        await updateEmail(user, emailToLink);
      } catch (emailError) {
        // If email update fails, try to continue anyway (might already be set)
        if (emailError.code !== "auth/email-already-in-use") {
          console.warn("Could not update email:", emailError);
        }
      }
    }

    // Create email/password credential and link it
    try {
      const credential = EmailAuthProvider.credential(emailToLink, password);
      await linkWithCredential(user, credential);
      
      // Update Firestore with the email so login works later
      await updateSeller(user.uid, { email: emailToLink });
      
      return { success: true, error: null };
    } catch (linkError) {
      // Handle specific errors
      if (linkError.code === "auth/email-already-in-use") {
        return { 
          success: false, 
          error: "This email is already associated with another account. Please use a different email or login with that account." 
        };
      } else if (linkError.code === "auth/weak-password") {
        return { 
          success: false, 
          error: "Password is too weak. Please choose a stronger password." 
        };
      } else if (linkError.code === "auth/invalid-email") {
        return { 
          success: false, 
          error: "Invalid email address. Please check and try again." 
        };
      }
      return { 
        success: false, 
        error: linkError.message || "Failed to set password. Please try again." 
      };
    }
  } catch (error) {
    return { 
      success: false, 
      error: error.message || "Failed to link email and password. Please try again." 
    };
  }
};

// Reset password via OTP - sends OTP to phone for password reset
export const resetPasswordViaOTP = async (phoneNumber) => {
  try {
    // Format phone number with +91 if not already formatted
    const formattedPhone = phoneNumber.startsWith("+") ? phoneNumber : `+91${phoneNumber}`;
    
    // Check if seller exists
    const sellerExists = await checkSellerExistsByPhone(formattedPhone);
    if (!sellerExists) {
      return { confirmationResult: null, error: "No seller account found with this phone number" };
    }

    // Reuse sendOTP logic but don't require seller check (already done)
    // We'll use sendOTPForSignup logic but with seller check
    await ensureDOMReady();
    setupRecaptchaContainer();
    cleanupRecaptchaVerifier();

    let recaptchaVerifier;
    try {
      setupRecaptchaContainer();
      recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => {
          cleanupRecaptchaVerifier();
        },
      });
      window.recaptchaVerifier = recaptchaVerifier;
    } catch (verifierError) {
      cleanupRecaptchaVerifier();
      if (verifierError?.message?.includes("already been rendered") || 
          verifierError?.message?.includes("already rendered")) {
        cleanupRecaptchaVerifier();
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
          setupRecaptchaContainer();
          recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
            size: "invisible",
            callback: () => {},
            "expired-callback": () => {
              cleanupRecaptchaVerifier();
            },
          });
          window.recaptchaVerifier = recaptchaVerifier;
        } catch (retryError) {
          const errorMsg = getRecaptchaErrorMessage(retryError);
          return { confirmationResult: null, error: errorMsg };
        }
      } else {
        const errorMsg = getRecaptchaErrorMessage(verifierError);
        return { confirmationResult: null, error: errorMsg };
      }
    }

    try {
      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
      return { confirmationResult, error: null };
    } catch (otpError) {
      cleanupRecaptchaVerifier();
      const errorMsg = getRecaptchaErrorMessage(otpError);
      return { confirmationResult: null, error: errorMsg };
    }
  } catch (error) {
    cleanupRecaptchaVerifier();
    if (error.message && error.message.includes("browser environment")) {
      return { confirmationResult: null, error: "This feature requires a browser environment." };
    }
    const errorMsg = getRecaptchaErrorMessage(error);
    return { confirmationResult: null, error: errorMsg };
  }
};

// Set password after OTP verification (for password reset)
export const setPasswordAfterOTPVerification = async (user, newPassword, email) => {
  try {
    const providers = user.providerData.map(p => p.providerId);
    const hasPasswordProvider = providers.includes("password");
    
    // If user has email/password provider, update password
    if (hasPasswordProvider) {
      try {
        await updatePassword(user, newPassword);
        
        // Ensure Firestore has the email linked to this user so login works correctly
        if (user.email) {
           await updateSeller(user.uid, { email: user.email });
        }
        
        return { success: true, error: null };
      } catch (error) {
        // If updatePassword fails due to recent login requirement, try reauthentication
        if (error.code === "auth/requires-recent-login") {
          return { 
            success: false, 
            error: "Please verify OTP again to reset your password." 
          };
        }
        return { success: false, error: error.message || "Failed to update password" };
      }
    } else {
      // User only has phone auth - need to link email/password credential
      // Get email from parameter or Firestore
      let userEmail = email;
      
      if (!userEmail) {
        // Try to get email from user object
        userEmail = user.email || undefined;
        
        // If still no email, get from Firestore using phone number
        if (!userEmail) {
          const phoneProvider = user.providerData.find(p => p.phoneNumber);
          if (phoneProvider?.phoneNumber) {
            const sellerEmail = await getSellerEmailByPhone(phoneProvider.phoneNumber);
            userEmail = sellerEmail || undefined;
          }
        }
      }

      if (!userEmail) {
        // Allow placeholder generation if email is missing
        // This supports the optional email requirement
      }

      // Link email/password credential
      return await linkEmailPasswordToPhoneUser(user, userEmail, newPassword);
    }
  } catch (error) {
    return { 
      success: false, 
      error: error.message || "Failed to set password. Please try again." 
    };
  }
};

// Logout
export const logout = async () => {
  try {
    await signOut(auth);
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Handle post-authentication routing based on seller status
export const handlePostAuthRouting = async (user, phoneNumber) => {
  try {
    // Get phone number from parameter or user's provider data
    let phone = phoneNumber;
    if (!phone) {
      const phoneProvider = user.providerData.find(p => p.phoneNumber);
      phone = phoneProvider?.phoneNumber || undefined;
    }
    
    // Ensure user document exists in users collection
    if (phone) {
      const formattedPhone = phone.startsWith("+") ? phone : `+91${phone}`;
      await ensureUserDocument(user.uid, formattedPhone, user.email || undefined);
    }

    // Check for seller document by userId field first, then by sellerId
    const { seller, error } = await checkSellerByUserId(user.uid);
    
    if (error) {
      return { route: "/auth/login", error };
    }

    if (!seller) {
      // No seller document found - redirect to onboarding
      return { route: "/onboarding", error: null };
    }

    // Seller exists - check status (already normalized by checkSellerByUserId)
    // Default to dashboard if status is missing (legacy accounts) or explicitly approved
    if (!seller.status || seller.status === "approved") {
      return { route: "/dashboard", error: null };
    } else if (seller.status === "pending") {
      return { route: "/waiting-approval", error: null };
    } else if (seller.status === "rejected") {
      return { route: "/onboarding?status=rejected", error: null };
    } else {
      // For any other status, default to dashboard as well to be safe, 
      // or onboarding if we really want to restrict. 
      // Given user feedback, let's err on side of letting them in.
      return { route: "/dashboard", error: null };
    }
  } catch (error) {
    console.error("Error in post-auth routing:", error);
    return { route: "/auth/login", error: error.message };
  }
};

// Auth state hook
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set a timeout to ensure loading doesn't stay true forever
    // Reduced from 5s to 2s for faster initial load
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 2000); // 2 second timeout

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimeout(timeout);
      setUser(user);
      setLoading(false);
    });

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  return { user, loading };
};
